import React, { useEffect, useState, useRef } from 'react';

// Custom CSS for editor
const editorStyles = `
  .rich-text-editor {
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.6;
  }
  
  .rich-text-editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .rich-text-editor-toolbar button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid #e2e8f0;
    background-color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    color: #374151;
    font-weight: 500;
  }
  
  .rich-text-editor-toolbar button:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }
  
  .rich-text-editor-toolbar button.active {
    background-color: #e2e8f0;
    border-color: #94a3b8;
    color: #1e293b;
  }
  
  /* Group formatting buttons */
  .rich-text-editor-toolbar .format-group {
    display: flex;
    gap: 4px;
    margin-right: 8px;
  }
  
  /* Style for heading buttons */
  .rich-text-editor-toolbar .heading-button {
    font-weight: bold;
  }
  
  /* Style for formatting buttons */
  .rich-text-editor-toolbar .format-button.bold {
    font-weight: bold;
  }
  
  .rich-text-editor-toolbar .format-button.italic {
    font-style: italic;
  }
  
  .rich-text-editor-toolbar .format-button.underline {
    text-decoration: underline;
  }
  
  /* Style for list buttons */
  .rich-text-editor-toolbar .list-button {
    font-size: 12px;
  }
  
  /* Style for special buttons */
  .rich-text-editor-toolbar .special-button {
    font-size: 16px;
  }
  
  .rich-text-editor-content {
    min-height: 250px;
    max-height: 500px;
    overflow-y: auto;
    padding: 16px;
    border: none;
    outline: none;
  }
  
  .rich-text-editor-content p {
    margin-bottom: 1em;
  }
  
  .rich-text-editor-content h1 {
    font-size: 1.8em;
    font-weight: bold;
    margin-top: 1em;
    margin-bottom: 0.5em;
  }
  
  .rich-text-editor-content h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin-top: 1em;
    margin-bottom: 0.5em;
  }
  
  .rich-text-editor-content h3 {
    font-size: 1.2em;
    font-weight: bold;
    margin-top: 1em;
    margin-bottom: 0.5em;
  }
  
  .rich-text-editor-content ul, 
  .rich-text-editor-content ol {
    padding-left: 1.5em;
    margin-top: 0.5em;
    margin-bottom: 1em;
  }
  
  .rich-text-editor-content li {
    margin-bottom: 0.7em;
    padding-left: 0.3em;
  }
  
  .rich-text-editor-content li + li {
    margin-top: 0.5em;
  }
  
  .rich-text-editor-content blockquote {
    border-left: 3px solid #ccc;
    padding-left: 1em;
    margin-left: 0;
    margin-right: 0;
    margin-top: 1em;
    margin-bottom: 1em;
    font-style: italic;
  }
  
  /* Dark mode support */
  [data-theme="dark"] .rich-text-editor-toolbar {
    background-color: #1e293b;
    border-color: #334155;
  }
  
  [data-theme="dark"] .rich-text-editor-toolbar button {
    background-color: #334155;
    border-color: #475569;
    color: #e2e8f0;
  }
  
  [data-theme="dark"] .rich-text-editor-toolbar button:hover {
    background-color: #475569;
    border-color: #64748b;
  }
  
  [data-theme="dark"] .rich-text-editor-toolbar button.active {
    background-color: #64748b;
    border-color: #94a3b8;
    color: #f8fafc;
  }
`;

interface RichTextEditorProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  value, 
  onChange, 
  placeholder = "Escreva seu conteúdo aqui..." 
}) => {
  const [editorContent, setEditorContent] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<{
    range: Range;
    selection: Selection;
  } | null>(null);
  const isUpdatingRef = useRef(false);
  const contentChangeTimeoutRef = useRef<number | null>(null);
  
  // Add custom styles on mount
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = editorStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Combine HTML array into a single string for editor
  useEffect(() => {
    try {
      // Skip if we're in the middle of an update
      if (isUpdatingRef.current) return;
      
      const combinedHtml = value.join('');
      
      // Only update if content has changed
      if (combinedHtml !== editorContent) {
        setEditorContent(combinedHtml);
        
        if (editorRef.current) {
          editorRef.current.innerHTML = combinedHtml || `<p>${placeholder}</p>`;
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  }, [value, placeholder, editorContent]);
  
  // Save the current selection
  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    savedSelectionRef.current = { range, selection };
  };
  
  // Restore the saved selection
  const restoreSelection = () => {
    if (!savedSelectionRef.current) return;
    
    const { range, selection } = savedSelectionRef.current;
    
    try {
      selection.removeAllRanges();
      selection.addRange(range.cloneRange());
    } catch (e) {
      console.error('Error restoring selection:', e);
    }
  };
  
  // Handle content change with debounce
  const handleContentChange = () => {
    if (!editorRef.current) return;
    
    // Clear any existing timeout
    if (contentChangeTimeoutRef.current) {
      window.clearTimeout(contentChangeTimeoutRef.current);
    }
    
    // Set a new timeout to update content
    contentChangeTimeoutRef.current = window.setTimeout(() => {
      if (!editorRef.current) return;
      
      // Mark that we're updating to prevent loops
      isUpdatingRef.current = true;
      
      const content = editorRef.current.innerHTML;
      
      if (!content || content === `<p>${placeholder}</p>` || content === '<p></p>') {
        onChange(['<p></p>']);
      } else {
        // Split content into paragraphs and block elements
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        
        // Get all direct children of the body
        const blocks = Array.from(doc.body.children).map(node => node.outerHTML);
        
        // If no blocks, add an empty paragraph
        if (blocks.length === 0) {
          blocks.push('<p></p>');
        }
        
        onChange(blocks);
      }
      
      // Reset updating flag after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 10);
    }, 500); // 500ms debounce
  };
  
  // Format the selected text with the specified tag
  const formatText = (tagName: string) => {
    // Restore selection before formatting
    restoreSelection();
    
    if (!editorRef.current) return;
    
    // Focus the editor
    editorRef.current.focus();
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    // Get the range
    const range = selection.getRangeAt(0);
    
    // Create a new document fragment to hold our formatted content
    const fragment = document.createDocumentFragment();
    
    // Create a temporary div to hold the selected content
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(range.cloneContents());
    
    // Process each node in the selection
    const processNode = (node: Node, parent: Node | DocumentFragment) => {
      // If it's a block element we want to convert
      if (node.nodeType === Node.ELEMENT_NODE && 
          ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'BLOCKQUOTE'].includes((node as Element).tagName)) {
        // Create a new element with the desired tag
        const newElement = document.createElement(tagName);
        
        // Copy attributes
        Array.from((node as Element).attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });
        
        // Process children
        Array.from(node.childNodes).forEach(child => {
          processNode(child, newElement);
        });
        
        // Add to parent
        parent.appendChild(newElement);
      } 
      // If it's a text node or other element, just copy it
      else if (node.nodeType === Node.TEXT_NODE) {
        // If the parent is the fragment (top level) and it's a text node,
        // wrap it in the target tag
        if (parent === fragment) {
          const newElement = document.createElement(tagName);
          newElement.appendChild(node.cloneNode(true));
          parent.appendChild(newElement);
        } else {
          parent.appendChild(node.cloneNode(true));
        }
      } 
      // For other elements, copy them and their children
      else {
        const newNode = node.cloneNode(false);
        Array.from(node.childNodes).forEach(child => {
          processNode(child, newNode);
        });
        parent.appendChild(newNode);
      }
    };
    
    // Process all nodes in the selection
    Array.from(tempDiv.childNodes).forEach(node => {
      processNode(node, fragment);
    });
    
    // Replace the selection with our new content
    range.deleteContents();
    range.insertNode(fragment);
    
    // Save the new selection
    saveSelection();
    
    // Trigger content change
    handleContentChange();
  };
  
  // Apply inline formatting
  const applyInlineFormat = (command: string, value: string = '') => {
    // Restore selection before formatting
    restoreSelection();
    
    if (!editorRef.current) return;
    
    // Focus the editor
    editorRef.current.focus();
    
    // Execute the command
    document.execCommand(command, false, value);
    
    // Save the new selection
    saveSelection();
    
    // Trigger content change
    handleContentChange();
  };
  
  // Check if a command is active
  const isCommandActive = (command: string) => {
    return document.queryCommandState(command);
  };
  
  // Get the current block format
  const getCurrentBlockFormat = () => {
    const value = document.queryCommandValue('formatBlock');
    return value.replace(/[<>]/g, '');
  };
  
  // Handle mouse down on toolbar buttons to prevent focus loss
  const handleToolbarButtonMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    saveSelection();
  };
  
  return (
    <div className="border border-input rounded-md bg-background overflow-hidden rich-text-editor">
      <div className="rich-text-editor-toolbar">
        <div className="format-group">
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => formatText('h1')}
            className={`heading-button ${getCurrentBlockFormat() === 'h1' ? 'active' : ''}`}
            title="Heading 1"
            aria-label="Heading 1"
          >
            H1
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => formatText('h2')}
            className={`heading-button ${getCurrentBlockFormat() === 'h2' ? 'active' : ''}`}
            title="Heading 2"
            aria-label="Heading 2"
          >
            H2
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => formatText('h3')}
            className={`heading-button ${getCurrentBlockFormat() === 'h3' ? 'active' : ''}`}
            title="Heading 3"
            aria-label="Heading 3"
          >
            H3
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => formatText('p')}
            className={`heading-button ${getCurrentBlockFormat() === 'p' ? 'active' : ''}`}
            title="Paragraph"
            aria-label="Paragraph"
          >
            P
          </button>
        </div>
        
        <div className="format-group">
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('bold')}
            className={`format-button bold ${isCommandActive('bold') ? 'active' : ''}`}
            title="Bold"
            aria-label="Bold"
          >
            B
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('italic')}
            className={`format-button italic ${isCommandActive('italic') ? 'active' : ''}`}
            title="Italic"
            aria-label="Italic"
          >
            I
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('underline')}
            className={`format-button underline ${isCommandActive('underline') ? 'active' : ''}`}
            title="Underline"
            aria-label="Underline"
          >
            U
          </button>
        </div>
        
        <div className="format-group">
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('insertOrderedList')}
            className={`list-button ${isCommandActive('insertOrderedList') ? 'active' : ''}`}
            title="Ordered List"
            aria-label="Ordered List"
          >
            OL
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('insertUnorderedList')}
            className={`list-button ${isCommandActive('insertUnorderedList') ? 'active' : ''}`}
            title="Unordered List"
            aria-label="Unordered List"
          >
            UL
          </button>
        </div>
        
        <div className="format-group">
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => {
              const url = prompt('Enter link URL:');
              if (url) applyInlineFormat('createLink', url);
            }}
            className="special-button"
            title="Insert Link"
            aria-label="Insert Link"
          >
            🔗
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => formatText('blockquote')}
            className="special-button"
            title="Blockquote"
            aria-label="Blockquote"
          >
            "
          </button>
          <button 
            type="button" 
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={() => applyInlineFormat('removeFormat')}
            className="special-button"
            title="Clear Formatting"
            aria-label="Clear Formatting"
          >
            ✕
          </button>
        </div>
      </div>
      <div
        ref={editorRef}
        className="rich-text-editor-content"
        contentEditable
        onInput={() => {
          // Save selection on input
          saveSelection();
        }}
        onKeyUp={() => {
          // Save selection and trigger content change
          saveSelection();
          handleContentChange();
        }}
        onMouseUp={() => {
          // Save selection on mouse up
          saveSelection();
        }}
        onBlur={() => {
          // Final content update on blur
          handleContentChange();
        }}
        dangerouslySetInnerHTML={{ __html: editorContent || `<p>${placeholder}</p>` }}
      />
    </div>
  );
};

export default RichTextEditor; 